import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAdminAuth } from '../adminAuth';

// Top-right account control. Replaces the old inline email + Sign out, which
// crammed (and wrapped) the mobile header. A circular person button opens a
// popover showing who's signed in and the Sign out action; a Settings link
// slots in later as a sibling .admin-account-item without touching this shell.
//
// The popover is the only positioned descendant of the header, so it paints
// above the page that scrolls under the (sticky, on mobile) header. It closes
// on outside pointerdown, Escape (focus returns to the button), and route
// change; Escape/arrows give it keyboard-menu semantics.
export function AdminAccountMenu() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the menu whenever the route changes (e.g. a future Settings link).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // While open: focus the first item, close on outside pointerdown or Escape
  // (returning focus to the toggle for keyboard users).
  useEffect(() => {
    if (!open) return;
    const focusFrame = window.requestAnimationFrame(() => {
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    });
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        toggleRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Roving focus across the menu items (correct once Settings joins Sign out).
  function onMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
    );
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    let next: number;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else if (e.key === 'ArrowDown') next = current < 0 ? 0 : (current + 1) % items.length;
    else next = current <= 0 ? items.length - 1 : current - 1;
    items[next]?.focus();
  }

  async function handleLogout() {
    setOpen(false);
    await logout();
    navigate('/admin/login', { replace: true });
  }

  const email = admin?.email ?? '';

  return (
    <div className="admin-account" ref={rootRef}>
      <button
        type="button"
        ref={toggleRef}
        className="admin-account-toggle tb-icon-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="admin-account-menu"
        aria-label="Account menu"
        onClick={() => setOpen((o) => !o)}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
        </svg>
      </button>
      {open && (
        <div
          className="admin-account-menu tb-menu"
          id="admin-account-menu"
          role="menu"
          aria-label="Account"
          ref={menuRef}
          onKeyDown={onMenuKeyDown}
        >
          <div className="admin-account-id">
            <span className="admin-account-label">Signed in as</span>
            <span className="admin-account-email" title={email}>
              {email}
            </span>
          </div>
          <div className="admin-account-divider tb-menu__sep" role="separator" />
          <button
            type="button"
            className="admin-account-item tb-menu__item"
            role="menuitem"
            onClick={handleLogout}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
